import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { Helper } from "src/app/core/helpers/helper";
import { MapViewService } from "../services/map-view.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { ToasterService } from "../services/toaster.service";
import { CommonService } from "../services/common.service";

@Component({
  selector: "app-driver-list",
  templateUrl: "./driver-list.component.html",
  styleUrl: "./driver-list.component.scss",
  standalone: false
})
export class DriverListComponent {
  @Output() driverDetails = new EventEmitter();
  @Input() driversList: any[] = [];

  driver: any;
  massNotificationForm!: FormGroup;
  uploadImage: any;
  notificationImageurl: any = null;
  modalConfig: any = { centered: true, backdrop: "static" };
  timeFormat?: string;

  @ViewChild("massNotificationModal", { static: true }) massNotificationModal!: TemplateRef<any>;

  constructor(
    public helper: Helper,
    private mapViewService: MapViewService,
    public offcanvasService: NgbOffcanvas,
    private toasterService: ToasterService,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.timeFormat = this.helper?.adminSettings?.timeFormat;
  }

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * Sets up a timeout to calculate scrollable height if the window width is greater than 991 pixels.
   * Adds an event listener to handle window resize events and adjust the scrollable height accordingly.
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      if (window.innerWidth > 991) {
        this.helper.calculateScorallableHeight();
      }
    }, 500);
    window.addEventListener("resize", ($event: any) => {
      if ($event.target.innerWidth > 991) {
        this.helper.calculateScorallableHeight();
      } else {
        const tableCustomScroll2 = document.querySelector(".custom-scroll-height") as HTMLElement;
        tableCustomScroll2.style.height = "auto";
      }
    });
  }

  /**
   * Emits the selected driver's details.
   *
   * @param driver The driver object whose details are to be emitted.
   */
  onDriverDetails(driver: any): void {
    this.driverDetails.emit(driver);
  }

  /**
   * Handles the error event for an image element.
   * Sets the source of the image to a default user image if an error occurs.
   *
   * @param event The error event object.
   */
  handleImageError(event: any): void {
    event.target.src = this.helper.DefaultImages.DefaultUserImage;
  }

  /**
   * Initializes the mass notification form with default form controls.
   */
  initMassNotificationForm(): void {
    this.massNotificationForm = new FormGroup({
      type: new FormControl<number | null>(this.helper.UserType.DRIVER, [Validators.required]),
      title: new FormControl<string | null>(null, [Validators.required]),
      message: new FormControl<string | null>(null, [Validators.required])
    });
  }

  /**
   * Opens the mass notification modal with the specified user details.
   * @param user The user details to send the mass notification.
   */
  openMassNotificationModal(driver: any): void {
    this.driver = driver;
    // Initialize the mass notification form
    this.initMassNotificationForm();

    this.offcanvasService.open(this.massNotificationModal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => {
        // Handle close/dismiss result if needed
      },
      () => {
        this.clearDefaultValues();
      }
    );
  }

  /**
   * Clears default values for the upload image and notification image URL.
   */
  clearDefaultValues(): void {
    this.uploadImage = null;
    this.notificationImageurl = null;
  }

  /**
   * Closes the currently open modal dialog.
   */
  closeModal(): void {
    this.offcanvasService.dismiss(this.massNotificationModal);
  }

  /**
   * Handles the selection of a notification image file.
   * Validates the file type and sets the upload image and notification image URL.
   *
   * @param event The file input change event.
   */
  onSelectNotificationImageFile(event: any): void {
    const notificationFiles = event.target.files;
    if (notificationFiles.length === 0) return;
    const notificationMimeType = notificationFiles[0].type;

    const notifiationFileType = this.helper.uploadFile.filter((element) => {
      return notificationMimeType === element;
    });

    if (!notifiationFileType.includes(notificationMimeType)) {
      this.toasterService.showNotification("error", this.helper.translateService.instant("validation-title.invalid-image-format"));
    } else {
      this.uploadImage = notificationFiles[0];
      const notificationReader = new FileReader();
      notificationReader.readAsDataURL(this.uploadImage);
      notificationReader.onload = (): void => {
        this.notificationImageurl = notificationReader.result;
      };
    }

    setTimeout(() => {
      event.target.value = "";
    }, 2000);
  }

  /**
   * Sends the mass notification using the data from the mass notification form.
   * If the notification is targeted to all countries or to customers, sets the city value to 'all'.
   * Checks the city validation based on the selected user type.
   * If the form is invalid, marks all controls as touched and returns.
   * Sends the mass notification and closes the modal on successful response.
   */
  sendMassNotification(): void {
    this.massNotificationForm.patchValue({
      message: this.massNotificationForm.value.message?.toString()?.trim()
    });

    this.massNotificationForm.patchValue({
      title: this.massNotificationForm.value.title?.toString()?.trim()
    });

    if (this.massNotificationForm.invalid) {
      this.massNotificationForm.markAllAsTouched();
      return;
    }

    const massNotificationFormValue: any = JSON.parse(JSON.stringify(this.massNotificationForm.value));

    const formData = this.setFormData(massNotificationFormValue);

    if (this.helper.showBtnLoader[1] === true) {
      return;
    }
    this.helper.showBtnLoader[1] = true;

    this.commonService.sendMassNotification(this.helper.UserType.DRIVER, formData).subscribe((response) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
      }
    });
  }

  /**
   * Sets the form data for the mass notification request.
   * Adds the upload image and user type ID if applicable.
   *
   * @param massNotificationFormValue The mass notification form values.
   * @returns The form data object.
   */
  setFormData(massNotificationFormValue: any): FormData {
    const formData = new FormData();
    for (const key in massNotificationFormValue) {
      if (massNotificationFormValue.hasOwnProperty(key)) {
        formData.set(key, massNotificationFormValue[key]);
      }
    }

    formData.set("imageUrl", this.uploadImage);
    formData.set("typeId", this.driver._id);

    return formData;
  }
}
